import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

import { facultyExamApi } from '../../api/examApi';
import { DashboardLayout } from '../../components/layout';
import { parseApiError } from '../../lib/apiErrors';

export default function EvaluationsPage() {
  const [pending, setPending] = useState([]);
  const [examIdByTitleSubject, setExamIdByTitleSubject] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPending();
  }, []);

  const groupedByExam = useMemo(() => {
    const groups = {};
    pending.forEach((item) => {
      const key = `${item.exam_title}__${item.exam_subject}`;
      if (!groups[key]) {
        groups[key] = {
          examTitle: item.exam_title,
          examSubject: item.exam_subject,
          items: [],
        };
      }
      groups[key].items.push(item);
    });
    return Object.values(groups);
  }, [pending]);

  const loadPending = async () => {
    try {
      // GET /evaluations/pending doesn't return exam_id, only exam_title/exam_subject —
      // resolve exam_id from this faculty's own exam list to link into the evaluation page.
      const [pendingResponse, examsResponse] = await Promise.all([
        facultyExamApi.getPendingEvaluations(),
        facultyExamApi.getMyExams(),
      ]);
      setPending(pendingResponse.data);
      const map = {};
      examsResponse.data.forEach((exam) => {
        map[`${exam.title}__${exam.subject}`] = exam.id;
      });
      setExamIdByTitleSubject(map);
    } catch (err) {
      toast.error('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthDownload = async (downloadFn, filename) => {
    try {
      const response = await downloadFn();
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(parseApiError(err, 'Download failed').message);
    }
  };

  const copyResultLink = async (resultToken) => {
    if (!resultToken) return;
    const link = `${window.location.origin}/result/${resultToken}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Result link copied');
    } catch {
      toast.error('Could not copy result link');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Evaluations">
        <div className="space-y-4 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-lg bg-slate-200 dark:bg-slate-700" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Evaluations">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Evaluations</h2>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Open an attempt to view the submitted paper, grade it, and publish a corrected copy —
            or download the raw files directly.
          </p>
        </div>

        {pending.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white py-12 text-center dark:border-slate-700 dark:bg-slate-800">
            <p className="text-slate-500 dark:text-slate-400">No submitted papers are waiting for evaluation.</p>
          </div>
        ) : (
          groupedByExam.map((group) => (
            <div key={`${group.examTitle}_${group.examSubject}`} className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
              <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">{group.examTitle}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{group.examSubject}</p>
              </div>
              <div className="space-y-4 p-5">
                {group.items.map((item) => {
                  const resultUrl = `${window.location.origin}/result/${item.result_token}`;
                  const examId = examIdByTitleSubject[`${item.exam_title}__${item.exam_subject}`];
                  return (
                    <div key={item.attempt_id} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">{item.student_name}</h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {item.student_class || 'Class not provided'} | {item.phone_number || item.student_email}
                          </p>
                          <p className="mt-1 text-xs text-slate-400">Submitted: {new Date(item.submitted_at).toLocaleString()}</p>
                          <p className="mt-1 text-xs text-amber-600 dark:text-amber-300">
                            Violations: {item.violation_count}
                            {item.violation_details && Object.keys(item.violation_details).length > 0 ? ` | ${Object.entries(item.violation_details).map(([key, value]) => `${key}: ${value}`).join(', ')}` : ''}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {(item.submissions || []).map((s, i) => (
                            <button
                              key={s.id}
                              onClick={() => handleAuthDownload(
                                () => facultyExamApi.downloadSubmission(s.id),
                                `submission_${i + 1}.pdf`
                              )}
                              className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                            >
                              <Download size={16} />
                              Paper {i + 1}{s.is_late ? ' (late)' : ''}
                            </button>
                          ))}
                          {item.has_corrected_paper && (
                            <button
                              onClick={() => handleAuthDownload(
                                () => facultyExamApi.downloadCorrectedPaper(item.attempt_id),
                                'corrected_paper.pdf'
                              )}
                              className="inline-flex items-center gap-2 rounded-md bg-emerald-100 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300"
                            >
                              <Download size={16} />
                              View Corrected Paper
                            </button>
                          )}
                          <button
                            onClick={() => copyResultLink(item.result_token)}
                            className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                          >
                            <Copy size={16} />
                            Copy Result Link
                          </button>
                          {examId ? (
                            <Link
                              to={`/faculty/exams/${examId}/evaluations/${item.attempt_id}`}
                              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-light"
                            >
                              <ClipboardCheck size={16} />
                              Evaluate
                            </Link>
                          ) : (
                            <span
                              title="Could not resolve which exam this attempt belongs to"
                              className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-400 dark:bg-slate-700 dark:text-slate-500"
                            >
                              <ClipboardCheck size={16} />
                              Evaluate
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="mt-2 break-all text-xs text-slate-400">{resultUrl}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
